<template>
  <rv-step-sidebar-content
    data-automation="step-change-password-new__content"
    :title="$t('members.titles.changePassword')"
  >
    <input
      autocomplete="username"
      :model-value="member.email"
      style="display: none"
    >

    <!-- Each input should be wrapped in form to allow pass manager to put value in each input -->
    <form @submit.prevent.stop="">
      <rv-input
        v-model="passwords.new"
        autocomplete="new-password"
        class="q-mx-lg q-mb-lg"
        data-automation="step-change-password-new__new-password"
        :label="$t('members.titles.newPassword')"
        :show-error="false"
        :show-error-icon="true"
        :type="showNewPassword ? 'text' : 'password'"
        :validation="v$.new"
      >
        <template #after="{ focused, validation }">
          <rv-validation-tooltip
            data-automation="step-change-password-new__new-password__validation"
            :exclude="['required']"
            :label="$t('members.titles.passwordMustContain')"
            :model-value="focused"
            :validation="validation"
          />
        </template>

        <template #append>
          <q-icon
            v-show="!!passwords.new"
            class="cursor-pointer"
            data-automation="step-change-password-new__new-password__visibility-icon"
            :name="showNewPassword ? 'visibility' : 'visibility_off'"
            @click="showNewPassword = !showNewPassword"
          />
        </template>
      </rv-input>
    </form>

    <!-- Each input should be wrapped in form to allow pass manager to put value in each input -->
    <form @submit.prevent.stop="">
      <rv-input
        v-model="passwords.repeat"
        autocomplete="new-password-repeat"
        class="q-mx-lg q-mb-lg"
        data-automation="step-change-password-new__repeat-password"
        :label="$t('members.titles.newPasswordRepeat')"
        :show-error="false"
        :show-error-icon="true"
        :type="showRepeatPassword ? 'text' : 'password'"
        :validation="v$.repeat"
      >
        <template #after="{ focused, validation }">
          <rv-validation-tooltip
            data-automation="step-change-password-new__repeat-password__validation"
            :exclude="['required']"
            :label="$t('members.titles.passwordMustContain')"
            :model-value="focused"
            :validation="validation"
          />
        </template>

        <template #append>
          <q-icon
            v-show="!!passwords.repeat"
            class="cursor-pointer"
            data-automation="step-change-password-new__repeat-password__visibility-icon"
            :name="showRepeatPassword ? 'visibility' : 'visibility_off'"
            @click="showRepeatPassword = !showRepeatPassword"
          />
        </template>
      </rv-input>
    </form>

    <template #actions>
      <div class="full-width flex">
        <rv-button
          class="full-width q-mb-sm"
          color="primary"
          data-automation="step-change-password-new__save"
          :disable="v$.$invalid || loading"
          :label="$t('members.actions.savePassword')"
          :loading="loading"
          @click="onSavePassword"
        />

        <rv-button
          class="full-width"
          color="primary"
          data-automation="step-change-password-new__cancel"
          :label="$t('general.actions.cancel')"
          secondary
          @click="onReset"
        />
      </div>
    </template>
  </rv-step-sidebar-content>
</template>

<script lang="ts" setup>
import {
  useStepSidebar,
  ValidationBuilder,
} from '@robovision/rvai-base'
import useVuelidate from '@vuelidate/core'
import type { PropType } from 'vue'
import {
  computed,
  reactive,
  ref,
} from 'vue'
import type { Member } from '~domain/Member'
import { useMembersStore } from '~stores/useMembersStore'
import { EditMemberSidebarSteps } from './EditMemberSidebarSteps'
import StepPasswordSaved from './StepPasswordSaved.vue'


const props = defineProps({
  currentPassword: {
    required: true,
    type: String as PropType<string>,
  },
  member: {
    required: true,
    type: Object as PropType<Member>,
  },
})

const {
  onReset,
  pushStep,
} = useStepSidebar()
const { setNewPassword } = useMembersStore()


const loading = ref(false)
const passwords = reactive({
  new: '',
  repeat: '',
})
const showNewPassword = ref(false)
const showRepeatPassword = ref(false)

const rules = computed(() => ({
  new: ValidationBuilder
    .$t('members.titles.newPassword')
    .required
    .minLength(8, 'validation.minLength')
    .minUpperAndLowerCaseCount(1, 'validation.lowercaseAndUppercase')
    .minNumberCount(1, 'validation.numbersWithoutDetails')
    .minSpecialCount(1, 'validation.specialSymbols')
    .rules,
  repeat: ValidationBuilder
    .$t('members.titles.newPasswordRepeat')
    .required
    .minLength(8, 'validation.minLength')
    .minUpperAndLowerCaseCount(1, 'validation.lowercaseAndUppercase')
    .minNumberCount(1, 'validation.numbersWithoutDetails')
    .minSpecialCount(1, 'validation.specialSymbols')
    .sameAs('members.titles.newPassword', passwords.new)
    .rules,
}))
const v$ = useVuelidate(rules, passwords, { $autoDirty: true })

async function onSavePassword (): Promise<void> {
  try {
    loading.value = true

    const isValid = await v$.value.$validate()
    if (!isValid) return

    await setNewPassword({
      oldPassword: props.currentPassword,
      password: passwords.new,
    })

    pushStep({
      component: StepPasswordSaved,
      stepName: EditMemberSidebarSteps.PasswordSaved,
    })
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}

</script>
